import { Routes, Route } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HeroSection } from './components/HeroSection';
import { ProjectsSection } from './components/ProjectsSection';
import { AboutSection } from './components/AboutSection';
import { ContactSection } from './components/ContactSection';
import { UnderwaterTransition } from './components/UnderwaterTransition';
import { UnderwaterParticles } from './components/UnderwaterParticles';
import { AtmosphericGrain } from './components/AtmosphericGrain';
import { OceanDepthProvider } from './components/OceanDepth';
import { ClipItCaseStudy } from './components/ClipItCaseStudy';

function HomePage() {
  const { scrollYProgress } = useScroll();

  // Background color progression (underwater depth)
  // After hero: progressively darker from medium blue → deep navy → near-black
  const backgroundColor = useTransform(
    scrollYProgress,
    [0, 0.15, 0.35, 0.6, 0.85, 1],
    [
      // Hero: Initial surface color
      'linear-gradient(to bottom, #0a1628 0%, #0d1a2d 50%, #1a3a52 100%)',
      // After hero / shallow underwater: slightly darker blue
      'linear-gradient(to bottom, #091422 0%, #0c1728 50%, #152d42 100%)',
      // First projects / medium underwater: deeper blue
      'linear-gradient(to bottom, #071018 0%, #0a1420 50%, #0f2030 100%)',
      // Middle projects / deep water: very dark navy
      'linear-gradient(to bottom, #050c12 0%, #07101a 50%, #0a1622 100%)',
      // About / very deep: near-black navy
      'linear-gradient(to bottom, #030810 0%, #050a12 50%, #060d15 100%)',
      // Contact / deepest but with hint of warmth coming
      'linear-gradient(to bottom, #020608 0%, #03080a 50%, #040a0d 100%)',
    ]
  );

  // Particle density - only visible after hero (underwater) - reduced for performance
  const particleDensity = useTransform(
    scrollYProgress,
    [0, 0.15, 0.25, 0.5, 0.75, 1],
    [0, 0, 10, 15, 10, 6]
  );

  // Particles only appear after hero transition
  const particlesOpacity = useTransform(scrollYProgress, [0.12, 0.18], [0, 1]);

  // Warm glow at bottom (about/contact) - subtle intentional ending
  const bottomGlowOpacity = useTransform(scrollYProgress, [0.75, 0.85, 1], [0, 0.12, 0.18]);

  return (
    <div className="min-h-screen text-slate-50 relative overflow-x-hidden">
      {/* Dynamic underwater background */}
      <motion.div
        className="fixed inset-0 z-0"
        style={{
          background: backgroundColor,
          willChange: 'background',
        }}
      />

      {/* Subtle grain overlay */}
      <AtmosphericGrain />

      {/* Underwater particles - only visible after hero */}
      <motion.div
        className="fixed inset-0 pointer-events-none z-[5]"
        style={{
          opacity: particlesOpacity,
        }}
      >
        <UnderwaterParticles density={particleDensity} />
      </motion.div>

      {/* Warm glow at deepest section (about/contact) */}
      <motion.div
        className="fixed inset-0 pointer-events-none z-[3]"
        style={{ opacity: bottomGlowOpacity }}
      >
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(240,184,85,0.25) 0%, transparent 60%)',
          }}
        />
      </motion.div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-cyan-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center relative">
          <div className="absolute left-6 flex gap-8">
            <a href="#home" className="text-cyan-200/70 hover:text-cyan-400 transition-colors">
              Home
            </a>
            <a href="#projects" className="text-cyan-200/70 hover:text-cyan-400 transition-colors">
              Projects
            </a>
            <a href="#about" className="text-cyan-200/70 hover:text-cyan-400 transition-colors">
              About
            </a>
            <a href="#contact" className="text-cyan-200/70 hover:text-cyan-400 transition-colors">
              Contact
            </a>
          </div>
          <img
            src="/images/portfoliotitle.png"
            alt="Julian Matos"
            className="h-7"
            style={{
              filter: 'invert(85%) sepia(25%) saturate(450%) hue-rotate(330deg) brightness(105%)'
            }}
          />
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10">
        {/* Hero Section - contains ocean surface */}
        <HeroSection />

        {/* Underwater transition - brief passage beneath surface */}
        <UnderwaterTransition />

        {/* Everything below is underwater */}
        <ProjectsSection />
        <AboutSection />
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-slate-950/50 border-t border-cyan-900/30 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-cyan-200/60">
          <p>&copy; 2025 Julian Matos</p>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <OceanDepthProvider>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/projects/clipit" element={<ClipItCaseStudy />} />
      </Routes>
    </OceanDepthProvider>
  );
}
